// Sales copy for each paywalled feature. The paywall chrome is shared; only
// this content differs per feature, so a new paywalled feature needs an entry
// here plus a `<FeatureGate>` at its surface — not a new component.

// The paywall chrome's renderings. Declared once, and as a value so props can
// validate against it: the gate, the error card and the paywall all forward
// the same prop, so a variant added in one place must not be reachable only by
// bypassing the others.
export const PAYWALL_VARIANTS = ["full", "compact", "inline"] as const;
export type PaywallVariant = (typeof PAYWALL_VARIANTS)[number];

export interface PaywallFeature {
  name: string;
  description: string;
  // Marks a feature as planned rather than shipped, so the pitch can be honest
  // about what isn't built yet (rendered with a "+" instead of a check).
  upcoming?: boolean;
  // Which names carry the pitch in the `inline` band — a copy decision, not a
  // positional slice.
  highlight?: boolean;
}

export interface PaywallContent {
  // Nav/label short form, also the mailto subject.
  label: string;
  headline: string;
  body: string;
  // One-line form of `body` for the `inline` band; the headline sells the
  // outcome but never names the feature. Optional.
  summary?: string;
  features: PaywallFeature[];
}

export const SALES_EMAIL = "cs@fyr.ai";

// The flags API renders camelCase (DRF CamelCaseJSONRenderer) while the consent
// endpoints key on the flag's stored snake_case key. Mapped explicitly rather
// than derived: a transform that mis-round-trips one key would fail silently at
// the consent wall, and there are few enough flags to just state them.
const CONSENT_KEY_BY_FLAG: Record<string, string> = {
  aiChat: "ai_chat",
  aiDashboards: "ai_dashboards",
  aiRecommendations: "ai_recommendations",
  aiVisibility: "ai_visibility",
  aiContentGeneration: "ai_content_generation",
  mcpAccess: "mcp_access",
};

// The `inline` band fits about two names beside the headline.
export const BAND_FEATURE_LIMIT = 2;

export function consentKeyForFlag(flag: string): string | undefined {
  return CONSENT_KEY_BY_FLAG[flag];
}

export function paywallMailto(content: PaywallContent): string {
  const subject = encodeURIComponent(`${content.label} access`);
  return `mailto:${SALES_EMAIL}?subject=${subject}`;
}

export const PAYWALL_CONTENT: Record<string, PaywallContent> = {
  crm: {
    label: "Commerce",
    headline: "See your stores, projects and loyalty all in one place.",
    body:
      "Connect Omnium or Shopify and bring aggregated daily sales into Fyr — " +
      "by store, with zero PII. Compare store revenue, product mix and " +
      "returns, plus Omnium's project pipeline and loyalty, alongside your " +
      "existing E-commerce metrics.",
    features: [
      {
        name: "Per-store comparison",
        description: "Stores side-by-side across every dataset",
      },
      {
        name: "Project pipeline",
        description: "New → in-progress → completed / cancelled",
      },
      {
        name: "Loyalty vs guest",
        description: "Member share, AOV uplift, new registrations",
      },
      {
        name: "Returns tracking",
        description: "Return count, value and rate by store",
      },
      {
        name: "No PII ingestion",
        description: "Aggregate-only endpoints; no customer records",
      },
      {
        name: "Promotions · next phase",
        description: "Per-promotion redemptions, coverage, discount value",
        upcoming: true,
      },
    ],
  },

  cm360: {
    label: "Google CM360",
    headline: "Bring your ad server into the same view as your media buys.",
    body:
      "Connect Google Campaign Manager 360 and import impressions, clicks, " +
      "video metrics and Floodlight conversions per advertiser, campaign, " +
      "placement and ad. Compare served delivery against every other channel " +
      "you already run in Fyr.",
    features: [
      {
        name: "Served delivery",
        description: "Impressions, clicks and video metrics from the ad server",
      },
      {
        name: "Floodlight conversions",
        description: "Click-through and view-through, per activity",
      },
      {
        name: "Down to the ad",
        description: "Advertiser, campaign, placement and ad levels",
      },
      {
        name: "Blended reporting",
        description: "Side by side with search, social and DV360",
      },
    ],
  },

  budgetTool: {
    label: "Budget Tool",
    headline: "Plan budgets, then watch them pace themselves.",
    body:
      "Build budget plans across every channel, split them by period or " +
      "entity, and track spend against plan as it happens. Alerts fire " +
      "before you overspend, not after the invoice.",
    features: [
      {
        name: "Plans across channels",
        description: "One budget spanning every connected ad account",
      },
      {
        name: "Pacing analytics",
        description: "Spend vs plan, projected end-of-period landing",
      },
      {
        name: "Overspend alerts",
        description: "Threshold alerts to the recipients you choose",
      },
      {
        name: "Budget requests",
        description: "Request more budget and track the approval",
      },
      {
        name: "Rollover handling",
        description: "Carry underspend or overspend into the next period",
      },
    ],
  },

  aiVisibility: {
    label: "AI Visibility",
    headline: "Find out whether AI assistants recommend you.",
    body:
      "Buyers ask ChatGPT, Claude and Perplexity before they ask you. AI " +
      "Visibility runs curated prompts on a schedule and tracks how often " +
      "your brand and your competitors appear in the answers — and which " +
      "sources those answers cite.",
    features: [
      {
        name: "Share of voice",
        description: "Your brand vs competitors, tracked over time",
      },
      {
        name: "Across the assistants",
        description: "ChatGPT, Claude and Perplexity on one schedule",
      },
      {
        name: "Cited sources",
        description: "Which pages the answers are built from",
      },
      {
        name: "Per business area",
        description: "Measure the areas you actually compete in",
      },
      {
        name: "Prompt control",
        description: "Choose which prompts are tracked, and how often",
      },
    ],
  },

  mcpAccess: {
    label: "MCP Access",
    headline: "Bring your Fyr data into Claude, or any AI tool you use.",
    body:
      "MCP Access lets external AI tools read your marketing data directly, " +
      "so you can ask questions of it wherever you already work instead of " +
      "exporting spreadsheets.",
    features: [
      {
        name: "Connect your own tools",
        description: "Claude Desktop, Claude Code and any MCP client",
      },
      {
        name: "Your whole dataset",
        description: "Campaign metrics, analytics, social, ecommerce",
      },
      {
        name: "OAuth applications",
        description: "Managed per-application access you can revoke",
      },
      {
        name: "Scoped permissions",
        description: "Read-only by default, per-organization",
      },
    ],
  },

  aiChat: {
    label: "AI Chat",
    headline: "Ask your marketing data anything.",
    body:
      "A chat assistant with your accounts already loaded. Ask why last " +
      "week dipped, which campaigns are worth more budget, or what changed " +
      "since the last report — and get an answer built from your own data.",
    features: [
      {
        name: "Grounded in your data",
        description: "Answers built from your connected accounts",
      },
      {
        name: "Cross-channel questions",
        description: "One question spanning every platform you run",
      },
      {
        name: "Alongside your work",
        description: "Docked or floating, on any view in Fyr",
      },
      {
        name: "Builds as it answers",
        description: "Can create dashboards and widgets on request",
      },
    ],
  },

  aiDashboards: {
    label: "AI Dashboards",
    headline: "Describe the dashboard you want. Get it built.",
    body:
      "AI-powered templates take a prompt instead of a fixed layout. Say " +
      "what the dashboard should show and the widgets, metrics and layout " +
      "are generated for you — then you approve, retry or reject the result.",
    features: [
      {
        name: "Built from a prompt",
        description: "Plain-language description instead of manual setup",
      },
      {
        name: "You approve the result",
        description: "Review proposed changes before they go live",
      },
      {
        name: "Retry until it fits",
        description: "Regenerate without starting over",
      },
      {
        name: "Reusable templates",
        description: "Save an AI template and apply it to any organization",
      },
    ],
  },

  aiRecommendations: {
    label: "AI Recommendations",
    headline: "Wake up to what needs attention.",
    // Leads with the feature's name; the badge says only "Add-on".
    summary:
      "AI Recommendations: a daily AI pass over your ads, website, SEO " +
      "and social data.",
    body:
      "A daily pass over your advertising, website, SEO and social data " +
      "that surfaces prioritised, concrete suggestions — with the reasoning " +
      "and the numbers behind each one, tracked so you can see whether " +
      "acting on them worked.",
    features: [
      {
        name: "Generated daily",
        description: "A fresh prioritised set every morning",
        highlight: true,
      },
      {
        name: "Across every discipline",
        description: "Ads, website, SEO, social and ecommerce",
      },
      {
        name: "Concrete actions",
        description: "What to change, where, and why it matters",
      },
      {
        name: "Impact tracking",
        description: "Follows up on whether acting on it helped",
        highlight: true,
      },
    ],
  },
};
